import { useEffect, useState } from 'react';
import { ShieldCheck } from 'lucide-react';
import { ensurePass, DIFFICULTY } from '@/lib/anubis';

/**
 * Anubis-style anti-bot gate. The app shell does not mount until a valid
 * proof-of-work pass exists. See src/lib/anubis.ts for scope honesty:
 * this deters naive bots, it is not a server-enforced wall.
 */
export function PowGate({ children }: { children: React.ReactNode }) {
  const [tries, setTries] = useState<number | null>(null); // null = solving not started / already passed
  const [passed, setPassed] = useState(false);

  useEffect(() => {
    let alive = true;
    ensurePass(n => { if (alive) setTries(n); })
      .then(() => { if (alive) setPassed(true); })
      .catch(() => { if (alive) setPassed(true); }); // never lock out real users on gate errors
    return () => { alive = false; };
  }, []);

  if (passed) return <>{children}</>;

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'var(--rl-bg, #d8e0ea)', fontFamily: 'Tahoma, Verdana, sans-serif',
    }}>
      <div style={{
        background: '#fff', border: '1px solid #9db2c8', borderRadius: 3,
        padding: '28px 32px', maxWidth: 420, textAlign: 'center',
      }}>
        <ShieldCheck size={32} aria-hidden style={{ color: '#1d6aad' }} />
        <h1 style={{ fontSize: 15, margin: '12px 0 6px' }}>Making sure you&rsquo;re not a bot</h1>
        <p style={{ fontSize: 12, color: '#555', margin: '0 0 10px' }}>
          Anubis-style proof-of-work check. Your browser is solving a small puzzle
          (SHA-256, difficulty {DIFFICULTY}) — this takes a few seconds and only
          happens once a week.
        </p>
        <p style={{ fontSize: 11, color: '#888', fontFamily: 'monospace', margin: 0 }}>
          {tries === null ? 'Preparing challenge…' : `Hashing… ${tries.toLocaleString()} tries`}
        </p>
      </div>
    </div>
  );
}
